import axios, { endPoints } from 'utils/api';

// Library list, per-quiz detail, stats card data, and version history all
// live in one slice — they share the same parent resource and the editor
// pulls from the detail map after the list view places the user on a quiz.

const SET_LIST = 'quizzes/SET_LIST';
const SET_LIST_META = 'quizzes/SET_LIST_META';
const SET_LIST_LOADING = 'quizzes/SET_LIST_LOADING';
const SET_LIST_ERROR = 'quizzes/SET_LIST_ERROR';
const SET_STATS = 'quizzes/SET_STATS';
const SET_DETAIL = 'quizzes/SET_DETAIL';
const SET_VERSIONS = 'quizzes/SET_VERSIONS';
const REMOVE_FROM_LIST = 'quizzes/REMOVE_FROM_LIST';

export const setQuizzesList = (data) => ({ type: SET_LIST, data });
export const setQuizzesMeta = (meta) => ({ type: SET_LIST_META, meta });
export const setQuizzesLoading = (loading) => ({ type: SET_LIST_LOADING, loading });
export const setQuizzesError = (error) => ({ type: SET_LIST_ERROR, error });
export const setQuizzesStats = (stats) => ({ type: SET_STATS, stats });
export const setQuizDetail = (quiz) => ({ type: SET_DETAIL, quiz });
export const setQuizVersions = (quizId, versions) => ({ type: SET_VERSIONS, quizId, versions });
export const removeQuizFromList = (publicId) => ({ type: REMOVE_FROM_LIST, publicId });

const INITIAL_STATE = {
    list: [],
    listMeta: { total: 0, currentPage: 1, perPage: 8, lastPage: 1 },
    listLoading: false,
    listError: null,
    stats: { publishedCount: 0, draftCount: 0, totalCount: 0, totalCompletions: 0 },
    detail: {}, // publicId → full quiz
    versions: {} // publicId → versions[]
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function(state = INITIAL_STATE, action) {
    switch (action.type) {
        case SET_LIST:
            return { ...state, list: [...action.data], listError: null };

        case SET_LIST_META:
            return { ...state, listMeta: { ...state.listMeta, ...action.meta } };

        case SET_LIST_LOADING:
            return { ...state, listLoading: action.loading };

        case SET_LIST_ERROR:
            return { ...state, listError: action.error, listLoading: false };

        case SET_STATS:
            return { ...state, stats: { ...state.stats, ...action.stats } };

        case SET_DETAIL:
            return {
                ...state,
                detail: { ...state.detail, [action.quiz.publicId]: action.quiz }
            };

        case SET_VERSIONS:
            return {
                ...state,
                versions: { ...state.versions, [action.quizId]: [...action.versions] }
            };

        case REMOVE_FROM_LIST:
            return {
                ...state,
                list: state.list.filter((q) => q.publicId !== action.publicId)
            };

        default:
            return state;
    }
}

// ── Library ──────────────────────────────────────────────────────────────

export const fetchQuizzes = (params = {}) => async (dispatch) => {
    dispatch(setQuizzesLoading(true));

    try {
        const response = await axios.get(endPoints.quizzes, { params });

        dispatch(setQuizzesList(response.data.data || []));
        if (response.data.meta) dispatch(setQuizzesMeta(response.data.meta));
    } catch (error) {
        dispatch(setQuizzesError(error.response?.data?.message || error.message || 'Failed to load quizzes'));
    } finally {
        dispatch(setQuizzesLoading(false));
    }
};

export const fetchQuizzesStats = () => async (dispatch) => {
    try {
        const response = await axios.get(endPoints.quizzesStats);

        dispatch(setQuizzesStats(response.data || {}));
    } catch {
        // Stats are nice-to-have — failure here shouldn't surface to the user.
    }
};

// ── Detail / lifecycle ───────────────────────────────────────────────────

export const fetchQuiz = (publicId) => async (dispatch) => {
    const response = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(response.data));

    return response.data;
};

export const createQuiz = () => async (dispatch) => {
    const response = await axios.post(endPoints.quizzes, {});

    dispatch(setQuizDetail(response.data));

    return response.data;
};

// Header / settings autosave. Server bypasses validation (BR-06) so this is
// fire-and-forget per field change. Caller is responsible for debouncing.
export const patchQuiz = (publicId, patch) => async (dispatch) => {
    const response = await axios.patch(`${endPoints.quizzes}/${publicId}`, patch);

    dispatch(setQuizDetail(response.data));

    return response.data;
};

// Publish — full validation server-side. Throws on 422 so the editor can
// surface the aggregate error list.
export const publishQuiz = (publicId) => async (dispatch) => {
    const response = await axios.post(`${endPoints.quizzes}/${publicId}/publish`, {});

    // Server returns a status snapshot, not the full quiz — refetch to get
    // the read-only payload with publishedAt + currentVersion bumped.
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));

    return response.data;
};

export const archiveQuiz = (publicId) => async (dispatch) => {
    const response = await axios.patch(`${endPoints.quizzes}/${publicId}/archive`);

    dispatch(setQuizDetail(response.data));
    dispatch(removeQuizFromList(publicId));

    return response.data;
};

export const draftFromVersion = (publicId) => async (dispatch) => {
    const response = await axios.post(`${endPoints.quizzes}/${publicId}/draft-from-version`, {});

    dispatch(setQuizDetail(response.data));

    return response.data;
};

// ── Versions ─────────────────────────────────────────────────────────────

export const fetchQuizVersions = (publicId) => async (dispatch) => {
    const response = await axios.get(`${endPoints.quizzes}/${publicId}/versions`);

    dispatch(setQuizVersions(publicId, response.data.data || []));

    return response.data.data || [];
};

export const fetchQuizVersionSnapshot = (publicId, versionNumber) => async () => {
    const response = await axios.get(`${endPoints.quizzes}/${publicId}/versions/${versionNumber}`);

    return response.data;
};

// ── Question CRUD ────────────────────────────────────────────────────────

export const createQuestion = (publicId, body) => async (dispatch) => {
    const response = await axios.post(`${endPoints.quizzes}/${publicId}/questions`, body);
    // Refetch the whole quiz so position/sibling state stays consistent.
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));

    return response.data;
};

export const patchQuestion = (publicId, questionPublicId, patch) => async (dispatch) => {
    const response = await axios.patch(
        `${endPoints.quizzes}/${publicId}/questions/${questionPublicId}`,
        patch
    );
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));

    return response.data;
};

export const deleteQuestion = (publicId, questionPublicId) => async (dispatch) => {
    await axios.delete(`${endPoints.quizzes}/${publicId}/questions/${questionPublicId}`);
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));
};

// ── Answer CRUD ──────────────────────────────────────────────────────────

// On single-select, the server atomically clears siblings — the response
// shape on that path is { answer, siblingsUpdated[] }. We re-fetch the
// quiz so the slice always holds the authoritative shape (BR-03, gotcha #2).
export const createAnswer = (publicId, questionPublicId, body) => async (dispatch) => {
    const response = await axios.post(
        `${endPoints.quizzes}/${publicId}/questions/${questionPublicId}/answers`,
        body
    );
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));

    return response.data;
};

export const patchAnswer = (publicId, questionPublicId, answerPublicId, patch) => async (dispatch) => {
    const response = await axios.patch(
        `${endPoints.quizzes}/${publicId}/questions/${questionPublicId}/answers/${answerPublicId}`,
        patch
    );
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));

    return response.data;
};

export const deleteAnswer = (publicId, questionPublicId, answerPublicId) => async (dispatch) => {
    await axios.delete(
        `${endPoints.quizzes}/${publicId}/questions/${questionPublicId}/answers/${answerPublicId}`
    );
    const full = await axios.get(`${endPoints.quizzes}/${publicId}`);

    dispatch(setQuizDetail(full.data));
};
